import { SegmentsTrack } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { Clip } from '@/player-config/src/types/clip-types';
import {
  SegmentBoundaryTransition,
  SegmentTransitionBoundaryIds
} from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentTransition';
import { Segment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { getSegmentsTrack } from '@/player-config/src/utils/playerConfigGetters';

const START_BOUNDARY_KEY = '__START__';
const END_BOUNDARY_KEY = '__END__';

export type SegmentEdge = 'fromSegmentId' | 'toSegmentId';

/**
 * Gets the segments track from clip's player config, optionally initializing segmentTransitions.
 * Returns null if player config or segments track doesn't exist.
 */
const getSegmentTracksWithTransitions = (clip: Clip, initializeTransitions = false): SegmentsTrack | null => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return null;
  }

  const segmentTracks = getSegmentsTrack(playerConfig);
  if (!segmentTracks) {
    return null;
  }

  if (initializeTransitions && !segmentTracks.segmentTransitions) {
    segmentTracks.segmentTransitions = [];
  }

  return segmentTracks;
};

/**
 * Upserts a segment transition in the clip's player config.
 * If a transition with the same boundary already exists, it is replaced.
 */
export const upsertSegmentTransition = (clip: Clip, transition: SegmentBoundaryTransition): void => {
  if (!transition.fromSegmentId && !transition.toSegmentId) {
    throw new Error('Segment transition requires at least one boundary segment id');
  }
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    throw new Error('Player config not found');
  }

  const segmentTracks = getSegmentsTrack(playerConfig);
  if (!segmentTracks) {
    throw new Error('Segments track not found');
  }

  if (!segmentTracks.segmentTransitions) {
    segmentTracks.segmentTransitions = [];
  }

  const transitionBoundaryKey = getSegmentTransitionBoundaryKey(transition);
  segmentTracks.segmentTransitions = segmentTracks.segmentTransitions.filter(
    existing => getSegmentTransitionBoundaryKey(existing) !== transitionBoundaryKey
  );

  segmentTracks.segmentTransitions.push(transition);
};

/**
 * Removes a segment transition from the clip's player config based on boundary segment IDs.
 */
export const removeSegmentTransition = (clip: Clip, fromSegmentId?: string, toSegmentId?: string): void => {
  if (!fromSegmentId && !toSegmentId) {
    throw new Error('Segment transition removal requires at least one boundary segment id');
  }
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const segmentTracks = getSegmentsTrack(playerConfig);
  if (!segmentTracks?.segmentTransitions) {
    return;
  }

  const transitionBoundaryKey = getSegmentTransitionBoundaryKey({ fromSegmentId, toSegmentId });
  segmentTracks.segmentTransitions = segmentTracks.segmentTransitions.filter(
    transition => getSegmentTransitionBoundaryKey(transition) !== transitionBoundaryKey
  );
};

/**
 * Generates a unique key for a segment transition boundary.
 */
export const getSegmentTransitionBoundaryKey = ({
  fromSegmentId,
  toSegmentId
}: Pick<SegmentBoundaryTransition, SegmentEdge>): string => {
  return `${fromSegmentId || START_BOUNDARY_KEY}::${toSegmentId || END_BOUNDARY_KEY}`;
};

/**
 * Extracts boundary segment IDs from previous and next segments.
 */
export const getSegmentTransitionBoundaryIds = (
  previousSegment?: Segment | null,
  nextSegment?: Segment | null
): SegmentTransitionBoundaryIds | null => {
  const boundaryIds: { fromSegmentId?: string; toSegmentId?: string } = {
    ...(previousSegment ? { fromSegmentId: previousSegment.id } : {}),
    ...(nextSegment ? { toSegmentId: nextSegment.id } : {})
  };

  return boundaryIds.fromSegmentId || boundaryIds.toSegmentId ? (boundaryIds as SegmentTransitionBoundaryIds) : null;
};

/**
 * Checks if a transition is applied at the start boundary (intro to first visible segment).
 */
export const isSegmentTransitionAppliedAtStart = ({
  segmentTransitionBoundaryMap,
  firstVisibleSegment,
  introSegment
}: {
  segmentTransitionBoundaryMap: Set<string>;
  firstVisibleSegment?: Segment;
  introSegment?: Segment | null;
}): boolean => {
  const boundaryIds = getSegmentTransitionBoundaryIds(introSegment, firstVisibleSegment);
  if (!boundaryIds) {
    return false;
  }
  return segmentTransitionBoundaryMap.has(getSegmentTransitionBoundaryKey(boundaryIds));
};

/**
 * Checks if a transition is applied at the end boundary (last visible segment to outro).
 */
export const isSegmentTransitionAppliedAtEnd = ({
  segmentTransitionBoundaryMap,
  lastVisibleSegment,
  outroSegment
}: {
  segmentTransitionBoundaryMap: Set<string>;
  lastVisibleSegment?: Segment;
  outroSegment?: Segment | null;
}): boolean => {
  const boundaryIds = getSegmentTransitionBoundaryIds(lastVisibleSegment, outroSegment);
  if (!boundaryIds) {
    return false;
  }
  return segmentTransitionBoundaryMap.has(getSegmentTransitionBoundaryKey(boundaryIds));
};

/**
 * Gets a map of all segment transition boundaries from the clip data.
 */
export const getSegmentTransitionBoundaryMap = (clipData: Clip): Set<string> => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return new Set<string>();
  }

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const segmentTransitions = segmentsTrack?.segmentTransitions || [];
  return new Set(segmentTransitions.map(transition => getSegmentTransitionBoundaryKey(transition)));
};

const createTransitionStateResult = ({
  segmentTransitionBoundaryMap,
  firstVisibleSegment,
  lastVisibleSegment,
  introSegment,
  outroSegment
}: {
  segmentTransitionBoundaryMap: Set<string>;
  firstVisibleSegment?: Segment;
  lastVisibleSegment?: Segment;
  introSegment?: Segment | null;
  outroSegment?: Segment | null;
}) => {
  const hasTransitions = segmentTransitionBoundaryMap.size > 0;
  return {
    segmentTransitionBoundaryMap,
    firstVisibleSegment,
    lastVisibleSegment,
    isStartTransitionApplied: hasTransitions
      ? isSegmentTransitionAppliedAtStart({
          segmentTransitionBoundaryMap,
          firstVisibleSegment,
          introSegment
        })
      : false,
    isEndTransitionApplied: hasTransitions
      ? isSegmentTransitionAppliedAtEnd({
          segmentTransitionBoundaryMap,
          lastVisibleSegment,
          outroSegment
        })
      : false
  };
};

/**
 * Gets the current state of segment transitions for the visible segments.
 */
export const getSegmentTransitionState = ({
  clipData,
  visibleSegments,
  introSegment,
  outroSegment,
  isEnabled = true
}: {
  clipData: Clip;
  visibleSegments: Segment[];
  introSegment?: Segment | null;
  outroSegment?: Segment | null;
  isEnabled?: boolean;
}) => {
  const firstVisibleSegment = visibleSegments[0];
  const lastVisibleSegment = visibleSegments[visibleSegments.length - 1];

  if (!isEnabled) {
    return createTransitionStateResult({
      segmentTransitionBoundaryMap: new Set<string>(),
      firstVisibleSegment,
      lastVisibleSegment,
      introSegment,
      outroSegment
    });
  }

  try {
    const segmentTransitionBoundaryMap = getSegmentTransitionBoundaryMap(clipData);
    return createTransitionStateResult({
      segmentTransitionBoundaryMap,
      firstVisibleSegment,
      lastVisibleSegment,
      introSegment,
      outroSegment
    });
  } catch (error) {
    return createTransitionStateResult({
      segmentTransitionBoundaryMap: new Set<string>(),
      firstVisibleSegment,
      lastVisibleSegment,
      introSegment,
      outroSegment
    });
  }
};

export const isNullish = (value): value is null | undefined => value === undefined || value === null;

const updateTransitions = (
  clip: Clip,
  transformer: (transition: SegmentBoundaryTransition) => SegmentBoundaryTransition | null,
  initializeIfMissing = false
): void => {
  const segmentTracks = getSegmentTracksWithTransitions(clip, initializeIfMissing);
  if (!segmentTracks?.segmentTransitions) {
    return;
  }

  segmentTracks.segmentTransitions = segmentTracks.segmentTransitions
    .map(transformer)
    .filter((t): t is SegmentBoundaryTransition => t !== null);
};

/**
 * Updates transitions when a segment is added (e.g., intro/outro).
 * Updates transitions with undefined/null edge to reference the new segment.
 * For example, when intro is added, transitions with undefined fromSegmentId will be updated to reference 'intro'.
 *
 * @param clip - The clip object to update
 * @param edge - The edge to update ('fromSegmentId' or 'toSegmentId')
 * @param segmentId - The ID of the segment being added
 */
export const updateTransitionsWhenSegmentAdded = (clip: Clip, edge: SegmentEdge, segmentId: string): void =>
  updateTransitions(
    clip,
    transition => (isNullish(transition[edge]) ? { ...transition, [edge]: segmentId } : transition),
    true
  );

/**
 * Updates transitions when a segment is removed (e.g., intro/outro).
 * Removes the edge from transitions that reference the segment, or removes the transition if it becomes invalid.
 * For example, when intro is removed, transitions with fromSegmentId='intro' will have fromSegmentId set to undefined.
 * If both edges become undefined, the transition is removed entirely.
 *
 * @param clip - The clip object to update
 * @param edge - The edge to update ('fromSegmentId' or 'toSegmentId')
 * @param segmentId - The ID of the segment being removed
 */
export const updateTransitionsWhenSegmentRemoved = (clip: Clip, edge: SegmentEdge, segmentId: string): void =>
  updateTransitions(clip, transition => {
    if (transition[edge] !== segmentId) {
      return transition;
    }
    const updated = { ...transition, [edge]: undefined };
    return updated.fromSegmentId || updated.toSegmentId ? updated : null;
  });

/**
 * Removes all transitions that reference a specific segment ID from either fromSegmentId or toSegmentId.
 * This is used when a segment is deleted to clean up all related transitions.
 *
 * @param clip - The clip object to update
 * @param segmentId - The ID of the segment being removed
 */
export const removeTransitionsForSegment = (clip: Clip, segmentId: string): void =>
  updateTransitions(clip, transition =>
    transition.fromSegmentId === segmentId || transition.toSegmentId === segmentId ? null : transition
  );

/**
 * Updates transitions when a segment is split.
 * When segment A is split into A1 (old segment) and A2 (new segment):
 * - Transitions with fromSegmentId = A are updated to fromSegmentId = A2 (since A2 is the part that transitions to the next segment)
 * - Transitions with toSegmentId = A are kept as toSegmentId = A1 (since transitions come into the first part)
 *
 * @param clip - The clip object to update
 * @param oldSegmentId - The ID of the original segment before split
 * @param newSegmentId - The ID of the new segment created after split
 */
export const updateTransitionsWhenSegmentSplit = (clip: Clip, oldSegmentId: string, newSegmentId: string): void =>
  updateTransitions(clip, transition =>
    transition.fromSegmentId === oldSegmentId ? { ...transition, fromSegmentId: newSegmentId } : transition
  );
